// Sawyer 12 incident-wording-pet-notes (F3) — the tenant's own word for the
// incident log, as every admin surface renders it. Pure: no React, no fetch.
//
// Default copy is byte-identical to what shipped before this lane (refuter C3:
// "nothing changes for anyone until an owner picks a word"). Only when an owner
// has configured a word do the verbs switch to neutral ones ("+ New report",
// "Save report"), because "+ Report report" or "File safety report" read badly.
// Both branches live in `incidentNouns()` so there is exactly one switch.
//
// The word rule mirrors the server's (`packages/core/tenant/get-tenant-terminology.ts`):
// trimmed, 1–30 characters, letters/digits in any script, then letters, digits,
// spaces, - ' & or /. The server refuses independently; this only keeps Save
// honest in the card.

export type VocabularySource = 'configured' | 'default';

export interface IncidentLabel {
  singular: string;
  plural: string;
}

export const DEFAULT_INCIDENT_LABEL: Readonly<IncidentLabel> = Object.freeze({
  singular: 'Incident',
  plural: 'Incidents',
});

export const MAX_VOCAB_WORD_LENGTH = 30;
export const VOCAB_WORD = /^[\p{L}\p{N}][\p{L}\p{N} '&/-]*$/u;

export const VOCAB_WORD_MESSAGES = {
  required: 'Enter both words, for example Report and Reports.',
  tooLong: `Keep it to ${MAX_VOCAB_WORD_LENGTH} characters.`,
  characters: "Use letters, numbers, spaces, - ' & or / only.",
} as const;

/** null when the word is acceptable; otherwise the reason shown under the field. */
export function validateVocabWord(value: string): string | null {
  const v = value.trim();
  if (v.length === 0) return VOCAB_WORD_MESSAGES.required;
  if (v.length > MAX_VOCAB_WORD_LENGTH) return VOCAB_WORD_MESSAGES.tooLong;
  if (!VOCAB_WORD.test(v)) return VOCAB_WORD_MESSAGES.characters;
  return null;
}

export interface IncidentNouns {
  source: VocabularySource;
  /** "Incident" / "Report" — headings, the detail h1. */
  one: string;
  /** "Incidents" / "Reports" — nav, list h1, dog-profile History. */
  many: string;
  /** "incident" / "safety report" — first character lowered only. */
  oneLower: string;
  manyLower: string;
  /** "an incident" / "a report" — article by the first letter. */
  aOne: string;
  /** List page primary button. Default: "+ Report incident". */
  newAction: string;
  /** New page h1. Default: "Report an incident". */
  newTitle: string;
  /** New page submit. Default: "File incident". */
  submit: string;
  /** New page error fallback. Default: "Failed to file the incident". */
  submitFailed: string;
  /** List empty line, before the optional " for this filter" and the period. Default: "No incidents reported". */
  emptyList: string;
}

/**
 * Lower the first character only ("Safety report" → "safety report"), and not
 * even that when the first word is an acronym (two or more capitals, e.g.
 * "Q&A / Log" stays "Q&A / Log", never "q&A").
 */
function lowerFirst(s: string): string {
  if (s.length === 0) return s;
  const firstWord = s.split(' ')[0] ?? '';
  const capitals = (firstWord.match(/\p{Lu}/gu) ?? []).length;
  if (capitals >= 2) return s;
  return s.charAt(0).toLocaleLowerCase() + s.slice(1);
}

/** "an" before a vowel letter (accents folded), else "a". "a Q&A / log", "an événement". */
export function articleFor(word: string): 'a' | 'an' {
  const first = word
    .trim()
    .charAt(0)
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase();
  return 'aeiou'.includes(first) && first.length === 1 ? 'an' : 'a';
}

export function incidentNouns(label: IncidentLabel | null | undefined, source: VocabularySource = 'default'): IncidentNouns {
  const configured = source === 'configured' && !!label;
  const l = configured ? label! : DEFAULT_INCIDENT_LABEL;
  const one = l.singular;
  const many = l.plural;
  const oneLower = lowerFirst(one);
  const manyLower = lowerFirst(many);
  const aOne = `${articleFor(one)} ${oneLower}`;
  if (!configured) {
    // Byte-identical to the strings that shipped before Sawyer F3.
    return {
      source: 'default',
      one,
      many,
      oneLower,
      manyLower,
      aOne,
      newAction: '+ Report incident',
      newTitle: 'Report an incident',
      submit: 'File incident',
      submitFailed: 'Failed to file the incident',
      emptyList: 'No incidents reported',
    };
  }
  return {
    source: 'configured',
    one,
    many,
    oneLower,
    manyLower,
    aOne,
    newAction: `+ New ${oneLower}`,
    newTitle: `New ${oneLower}`,
    submit: `Save ${oneLower}`,
    submitFailed: `Failed to save the ${oneLower}`,
    emptyList: `No ${manyLower} on file`,
  };
}

export const DEFAULT_INCIDENT_NOUNS: IncidentNouns = incidentNouns(null, 'default');
